import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Paper,
} from '@mui/material';
import { Speed as SpeedIcon, QrCode2 as QrCodeIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Login = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(0); // 0: login, 1: 2FA, 2: 2FA setup
  const [tempToken, setTempToken] = useState('');
  const [setupToken, setSetupToken] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [twoFASecret, setTwoFASecret] = useState('');
  const [twoFAQRCode, setTwoFAQRCode] = useState('');
  const { login, isAuthenticated, updateUser } = useAuth();
  const navigate = useNavigate();
  const { register, handleSubmit, formState: { errors } } = useForm();
  const { register: register2FA, handleSubmit: handleSubmit2FA, formState: { errors: errors2FA } } = useForm();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const onSubmit = async (data) => {
    setLoading(true);
    setError('');

    try {
      console.log('🔐 Login attempt for:', data.email);
      const result = await login(data.email, data.password);
      console.log('📡 Login response:', result);
      
      if (result.requires2FA) {
        // User has 2FA enabled, show 2FA step
        console.log('✅ User has 2FA enabled, showing 2FA step');
        setTempToken(result.tempToken);
        setUserEmail(data.email);
        setStep(1);
        toast.success('Please enter your 2FA token');
      } else if (result.requires2FASetup) {
        // New user needs to setup 2FA
        console.log('🆕 New user needs 2FA setup, triggering setup flow');
        console.log('🔑 Setup token:', result.setupToken);
        setSetupToken(result.setupToken);
        setUserEmail(data.email);
        // Automatically trigger 2FA setup
        console.log('🚀 Calling handle2FASetup...');
        await handle2FASetup(result.setupToken);
      } else if (result.requires2FAEnable) {
        // User has 2FA secret but not enabled, needs to complete setup
        console.log('🔧 User has incomplete 2FA setup, triggering setup flow');
        setSetupToken(result.setupToken);
        setUserEmail(data.email);
        // Automatically trigger 2FA setup
        await handle2FASetup(result.setupToken);
      } else if (result.success) {
        console.log('✅ Login successful, navigating to dashboard');
        toast.success('Login successful!');
        navigate('/dashboard');
      } else {
        console.log('❌ Login failed:', result.error);
        setError(result.error);
      }
    } catch (err) {
      console.error('💥 Login error:', err);
      setError('An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit2FA = async (data) => {
    setLoading(true);
    setError('');

    try {
      console.log('🔐 Submitting 2FA token:', data.token);
      console.log('🔑 Temp token:', tempToken ? 'Present' : 'Missing');
      
      const response = await fetch('/api/auth/verify-2fa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: data.token,
          tempToken: tempToken
        }),
      });

      const result = await response.json();
      console.log('📡 2FA verification response:', result);

      if (response.ok) {
        console.log('✅ 2FA verification successful!');
        console.log('👤 User data:', result.user);
        
        // Store the final token and user info
        localStorage.setItem('token', result.token);
        localStorage.setItem('user', JSON.stringify(result.user));
        console.log('💾 Token and user stored in localStorage');
        
        // Update the AuthContext with the new user info
        // This prevents the redirect loop by ensuring the context is updated
        if (result.user) {
          console.log('🔄 Updating AuthContext user state...');
          // Update the user state in AuthContext using the updateUser function
          updateUser(result.user);
          console.log('✅ AuthContext updated, navigating to dashboard...');
          toast.success('2FA verification successful!');
          navigate('/dashboard');
        } else {
          console.log('❌ No user data received');
          setError('No user data received from 2FA verification');
        }
      } else {
        console.log('❌ 2FA verification failed:', result.error);
        setError(result.error || '2FA verification failed');
      }
    } catch (err) {
      console.error('💥 2FA verification error:', err);
      setError('An unexpected error occurred during 2FA verification');
    } finally {
      setLoading(false);
    }
  };

  const handle2FASetup = async (token) => {
    try {
      console.log('🔐 Starting 2FA setup with token:', token ? 'Present' : 'Missing');
      setLoading(true);
      setError('');
      
      // Call the 2FA setup endpoint
      console.log('📡 Calling /api/auth/setup-2fa...');
      const response = await fetch('/api/auth/setup-2fa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          setupToken: token
        })
      });

      console.log('📡 Setup response status:', response.status);
      const result = await response.json();
      console.log('📡 Setup response data:', result);
      
      if (response.ok) {
        // Store 2FA data and show setup UI
        setTwoFASecret(result.secret);
        setTwoFAQRCode(result.qrCode);
        setStep(2); // Show 2FA setup step
        console.log('✅ 2FA setup successful, moving to step 2');
        toast.success('2FA setup completed - scan the QR code');
      } else {
        console.log('❌ 2FA setup failed:', result.error);
        setError(result.error || 'Failed to setup 2FA');
      }
    } catch (err) {
      console.error('💥 2FA setup error:', err);
      setError('An unexpected error occurred during 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const handle2FAEnable = async () => {
    setLoading(true);
    setError('');

    try {
      // Get the token from the input field
      const token = document.getElementById('setupToken').value;
      if (!token || token.length !== 6) {
        setError('Please enter a valid 6-digit 2FA token');
        setLoading(false);
        return;
      }

      // Call the 2FA enable endpoint
      const response = await fetch('/api/auth/enable-2fa', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          setupToken: setupToken,
          token: token
        })
      });

      const result = await response.json();

      if (response.ok) {
        console.log('✅ 2FA enabled successfully!');
        toast.success('2FA enabled successfully!');
        
        // Now that 2FA is enabled, we need to get a proper login token
        // The user should go back to login and enter their 2FA token
        setStep(0);
        setError('');
        setSetupToken('');
        setTwoFASecret('');
        setTwoFAQRCode('');
        
        toast.success('2FA setup complete! Please login again with your 2FA token.');
      } else {
        console.log('❌ 2FA enable failed:', result.error);
        setError(result.error || 'Failed to enable 2FA');
      }
    } catch (err) {
      console.error('💥 2FA enable error:', err);
      setError('An unexpected error occurred during 2FA enable');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          py: 4,
        }}
      >
        <Paper
          elevation={6}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 3,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <Box
              component="img"
              src="/cache_flow_rpdad.webp"
              alt="CacheFlow"
              sx={{
                height: '80px',
                width: 'auto',
                mb: 2,
                objectFit: 'contain'
              }}
            />
            <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 1 }}>
              CacheFlow
            </Typography>
            <Typography variant="body1" align="center" sx={{ opacity: 0.9, fontStyle: 'italic' }}>
              Turn traffic into profit — and keep it flowing.
            </Typography>
          </Box>
        </Paper>

        <Card sx={{ width: '100%', borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom align="center" fontWeight={600}>
              Sign In
            </Typography>
            
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {step === 0 && (
              // Login Step
              <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
              <TextField
                margin="normal"
                fullWidth
                id="email"
                label="Email Address"
                type="email"
                autoComplete="email"
                autoFocus
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                })}
                error={!!errors.email}
                helperText={errors.email?.message}
              />
              
              <TextField
                margin="normal"
                fullWidth
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 1,
                    message: 'Password must be at least 1 character',
                  },
                })}
                error={!!errors.password}
                helperText={errors.password?.message}
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Sign In'
                )}
              </Button>
            </Box>
            )}

            {step === 1 && !twoFAQRCode && (
              // 2FA Step
              <Box component="form" onSubmit={handleSubmit2FA(onSubmit2FA)} sx={{ mt: 2 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <QrCodeIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Two-Factor Authentication
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Enter the 6-digit code from your authenticator app
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Logging in as: <strong>{userEmail}</strong>
                  </Typography>
                </Box>

                <Box sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Enter the 6-digit code from your authenticator app
                  </Typography>
                  <input
                    type="tel"
                    id="token"
                    name="token"
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={6}
                    pattern="[0-9]*"
                    inputMode="numeric"
                    data-2fa="true"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-form-type="other"
                    data-keeperignore="true"
                    data-bwignore="true"
                    data-keeweb-ignore="true"
                    data-ignore="true"
                    data-autocomplete="one-time-code"
                    role="textbox"
                    aria-label="2FA authentication code"
                    spellCheck="false"
                    style={{
                      width: '100%',
                      padding: '16.5px 14px',
                      fontSize: '16px',
                      border: errors2FA.token ? '1px solid #d32f2f' : '1px solid rgba(0, 0, 0, 0.23)',
                      borderRadius: '4px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      fontFamily: 'inherit'
                    }}
                    {...register2FA('token', {
                      required: '2FA token is required',
                      pattern: {
                        value: /^[0-9]{6}$/,
                        message: 'Please enter a 6-digit code',
                      },
                    })}
                    onChange={(e) => {
                      // Only allow numbers
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      e.target.value = value;
                      // Trigger react-hook-form change
                      const event = { target: { name: 'token', value } };
                      register2FA('token').onChange(event);
                    }}
                  />
                  {errors2FA.token && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {errors2FA.token.message}
                    </Typography>
                  )}
                </Box>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2, py: 1.5 }}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Verify & Sign In'
                  )}
                </Button>

                <Button
                  type="button"
                  fullWidth
                  variant="outlined"
                  sx={{ mb: 2 }}
                  onClick={() => {
                    setStep(0);
                    setError('');
                    setTempToken('');
                  }}
                >
                  Back to Login
                </Button>
              </Box>
            )}

            {step === 2 && (
              // 2FA Setup Step
              <Box sx={{ mt: 2 }}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <QrCodeIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Complete 2FA Setup
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Scan the QR code with your authenticator app, then enter the token to enable 2FA
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Setting up 2FA for: <strong>{userEmail}</strong>
                  </Typography>
                </Box>

                {twoFAQRCode && (
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <img 
                      src={twoFAQRCode} 
                      alt="2FA QR Code" 
                      style={{ maxWidth: '200px', border: '1px solid #ddd', borderRadius: '8px' }}
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      <strong>Secret:</strong> {twoFASecret}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ mt: 2, mb: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    2FA Token from Authenticator App
                  </Typography>
                  <input
                    type="tel"
                    id="setupToken"
                    name="setupToken"
                    autoComplete="one-time-code"
                    autoFocus
                    maxLength={6}
                    pattern="[0-9]*"
                    inputMode="numeric"
                    data-2fa="true"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    data-form-type="other"
                    data-keeperignore="true"
                    data-bwignore="true"
                    data-keeweb-ignore="true"
                    data-ignore="true"
                    data-autocomplete="one-time-code"
                    role="textbox"
                    aria-label="2FA setup authentication code"
                    spellCheck="false"
                    placeholder="Enter 6-digit code"
                    style={{
                      width: '100%',
                      padding: '16.5px 14px',
                      fontSize: '16px',
                      border: '1px solid rgba(0, 0, 0, 0.23)',
                      borderRadius: '4px',
                      outline: 'none',
                      transition: 'border-color 0.2s',
                      fontFamily: 'inherit'
                    }}
                    onChange={(e) => {
                      // Only allow numbers
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      e.target.value = value;
                    }}
                  />
                </Box>

                <Button
                  type="button"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2, py: 1.5 }}
                  disabled={loading}
                  onClick={handle2FAEnable}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Enable 2FA & Complete Login'
                  )}
                </Button>

                <Button
                  type="button"
                  fullWidth
                  variant="outlined"
                  sx={{ mb: 2 }}
                  onClick={() => {
                    setStep(0);
                    setError('');
                    setSetupToken('');
                    setTwoFASecret('');
                    setTwoFAQRCode('');
                  }}
                >
                  Back to Login
                </Button>
              </Box>
            )}


          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default Login;
